// Ported from dev-scrips-pm-react src/shared/services/lookups.ts (PROD-1119 Patients data layer).
/**
 * Common value-set lookups served by `/api/Master/...`. Cross-domain — any
 * feature can consume these (patients, scheduling, billing settings).
 *
 * All entries share the `{ id, name }` shape Flutter calls `IdNameModel`.
 * Static lookups use a 1-hour staleTime; search-based lookups (Nationality,
 * Language, Relation, Religion) refetch on query change but keep their own
 * 5-minute cache so the second keystroke benefits when the user types and
 * deletes the same character.
 */

import { useQuery } from '@tanstack/react-query';
import { ApiError, get } from '@/shared/api/api-client';
import { warnDuplicateKeys } from '@/shared/lib/warn-duplicate-keys';

export interface LookupItem {
  id: string;
  name: string;
}

/**
 * Master-data entries that ship the FHIR `Code` shape (`{ code, display,
 * definition }`) instead of `{ id, name }`. Used by Patient Importance /
 * Residency Type / Education — the wire keys options by `code` and labels
 * by `display`. Keeping these distinct from `LookupItem` stops consumers
 * from binding `.id` / `.name` (both `undefined` here → blank options +
 * duplicate empty-string React keys).
 */
export interface MasterCodeItem {
  code: string;
  display: string;
  definition?: string | null;
  system?: string | null;
}

const ONE_HOUR = 60 * 60 * 1000;
const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * 404 = "no entries" — return an empty array instead of surfacing the
 * red error toast. Mirrors Flutter behaviour on master-data lookups
 * (the .dart consumers treat absence as empty list, not an error).
 * Used by all static lookups below since they're array-shaped.
 */
async function fetchArrayOrEmpty<TItem>(
  endpoint: string,
  signal: AbortSignal | undefined,
): Promise<TItem[]> {
  try {
    return await get<TItem[]>(endpoint, { signal });
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) return [];
    throw err;
  }
}

function staticLookup<TItem>(key: string, endpoint: string) {
  return useQuery({
    queryKey: ['lookup', key],
    queryFn: ({ signal }) => fetchArrayOrEmpty<TItem>(endpoint, signal),
    staleTime: ONE_HOUR,
  });
}

export function useGenders() {
  return staticLookup<LookupItem>('genders', '/api/Master/Gender');
}

export function useIdTypes() {
  return staticLookup<LookupItem>('id-types', '/api/Master/IdentityType');
}

export function useMaritalStatuses() {
  return staticLookup<LookupItem>('marital-statuses', '/api/Master/MaritalStatus');
}

/** Flutter calls this `Ownership` — backend endpoint is `OwnerType`. */
export function useOwnerTypes() {
  return staticLookup<LookupItem>('owner-types', '/api/Master/OwnerType');
}

/** Backend typo preserved: `/Insurar`, not `/Insurer`. Don't "fix" without
 *  a backend rename — the route only exists at the typo path. */
export function useInsurers() {
  return staticLookup<LookupItem>('insurers', '/api/Master/Insurar');
}

/**
 * Nationality / Religion search results ship the FHIR `Code` shape
 * (`{ code, display, system[, description, shortName] }`), NOT `{ id, name }`.
 * We expose `id`/`name` aliases (code/display) so the `MasterTypeahead`
 * (which keys by `id`, labels by `name`) renders correctly, while keeping
 * the full wire fields so the caller can persist the exact object. Without
 * the alias the options key by `undefined` → duplicate React keys + blank
 * labels (same family as the PatientImportance/Residency bug).
 */
export interface NationalityItem {
  id: string | null;
  name: string | null;
  code: string | null;
  display: string | null;
  system: string | null;
}

export interface ReligionItem extends NationalityItem {
  description: string | null;
  shortName: string | null;
}

export function useNationalities(query: string) {
  return useQuery({
    queryKey: ['lookup', 'nationalities', query],
    queryFn: async ({ signal }) => {
      const raw = await get<
        Array<{ code?: string; display?: string; name?: string; system?: string }>
      >(`/api/Master/Nationality?searchQuery=${encodeURIComponent(query)}`, {
        signal,
      });
      const items = (raw ?? []).map<NationalityItem>((r) => ({
        id: r.code ?? null,
        name: r.display ?? r.name ?? null,
        code: r.code ?? null,
        display: r.display ?? r.name ?? null,
        system: r.system ?? null,
      }));
      return warnDuplicateKeys(items, (i) => i.code, 'nationality');
    },
    staleTime: FIVE_MINUTES,
  });
}

/**
 * Language search. The endpoint does NOT ship `{ id, name }` — it ships the
 * value-set shape `[{ languageId, code: { code, displayName, system } }]`
 * (verified live 2026-05-14, see `settings/use-language-search.ts`). We map
 * to the `{ id, name }` shape `MasterTypeahead` keys/labels by, mirroring
 * Flutter `personal_details_form.dart:717-721`:
 *   patient.language     = languageId          (GUID)
 *   patient.languageName = { id: languageId, name: code.displayName }
 * Without this map the picker rendered blank rows and persisted `undefined`
 * for both `language` and `languageName.id`. `enabled` gate matches the
 * settings sibling hook — no empty-query fetch on mount.
 */
export function useLanguages(query: string) {
  const trimmed = query.trim();
  return useQuery({
    queryKey: ['lookup', 'languages', trimmed],
    queryFn: async ({ signal }) => {
      const raw = await get<
        Array<{
          languageId?: string;
          code?: { code?: string; displayName?: string; system?: string } | null;
        }>
      >(
        `/api/Master/LanguageValueSetList?searchQuery=${encodeURIComponent(trimmed)}`,
        { signal },
      );
      const items = (raw ?? []).map<LookupItem>((r) => ({
        id: r.languageId ?? '',
        name: r.code?.displayName ?? '',
      }));
      return warnDuplicateKeys(items, (i) => i.id, 'languages');
    },
    enabled: trimmed.length > 0,
    staleTime: FIVE_MINUTES,
  });
}

export function useReligions(query: string) {
  return useQuery({
    queryKey: ['lookup', 'religions', query],
    queryFn: async ({ signal }) => {
      const raw = await get<
        Array<{
          code?: string;
          display?: string;
          name?: string;
          system?: string;
          description?: string;
          shortName?: string;
        }>
      >(`/api/Master/Religion?searchQuery=${encodeURIComponent(query)}`, {
        signal,
      });
      const items = (raw ?? []).map<ReligionItem>((r) => ({
        id: r.code ?? null,
        name: r.display ?? r.name ?? null,
        code: r.code ?? null,
        display: r.display ?? r.name ?? null,
        system: r.system ?? null,
        description: r.description ?? null,
        shortName: r.shortName ?? null,
      }));
      return warnDuplicateKeys(items, (i) => i.code, 'religion');
    },
    staleTime: FIVE_MINUTES,
  });
}

/**
 * Education level lookup. Flutter `common_data_source_impl.dart:1515`
 * → `Master/Education` (i.e. `/api/Master/Education`). Returns `Code`
 * entries (`{ code, display, system }`) — normalised here to
 * `EducationCode` so consumers don't depend on the wider `Code` type.
 *
 * NOTE: an earlier URL `/api/EducationWorkExperience` 404'd (it doesn't
 * exist on the Master service) — the dropdown was silently empty. The
 * real route is `/api/Master/Education`, curl-verified 2026-06-04.
 *
 * "Other" is part of the list; selecting it should reveal a free-text
 * "tell us more" field per Flutter `education_form.dart:34`.
 *
 * AUTH (review-flagged by Samer on PROD-725 PR #50):
 * The `get()` helper from `@/shared/api/api-client` always injects the
 * Authorization + OrganizationId + PracticeId headers, so this call is
 * authenticated client-side. Server-side concern (out of frontend
 * scope): confirm `/api/EducationWorkExperience` is mounted inside the
 * tenant-scoped route group on the .NET side and not exposed as a
 * public/unauthenticated route. Common .NET minimal-API pitfall.
 */
export interface EducationCode {
  code: string;
  display: string;
  system?: string | null;
}

export function useEducationLevels() {
  return useQuery({
    queryKey: ['lookup', 'education-levels'],
    queryFn: ({ signal }) =>
      fetchArrayOrEmpty<EducationCode>('/api/Master/Education', signal),
    staleTime: ONE_HOUR,
  });
}

/**
 * `/api/Master/Relation` is the only Master lookup that ships PascalCase
 * keys (`{Id, Name, Code, System}` — FHIR Coding shape), while every other
 * `/api/Master/*` endpoint returns lowercase `{id, name}`. Normalize at the
 * boundary so consumers see the consistent `LookupItem` shape — verified
 * against dev curl 2026-05-15 (see PR #103 manual-verification thread).
 */
interface RelationWireItem {
  Id?: string;
  Name?: string;
  id?: string;
  name?: string;
}

export function useRelations(query: string, category?: string) {
  return useQuery({
    queryKey: ['lookup', 'relations', query, category],
    queryFn: async ({ signal }) => {
      const qs = new URLSearchParams({ searchQuery: query });
      if (category) qs.set('category', category);
      const raw = await get<RelationWireItem[]>(
        `/api/Master/Relation?${qs.toString()}`,
        { signal },
      );
      return (raw ?? []).map<LookupItem>((r) => ({
        id: r.Id ?? r.id ?? '',
        name: r.Name ?? r.name ?? '',
      }));
    },
    staleTime: FIVE_MINUTES,
  });
}
